/*
//-------------------------------------------------------
//   Copyright (c) 2019 Templa Computer Systems Ltd
//
//   App:        EmployeePortal.UI
//   Object:     Authentication
//   Details:    Authentication store
//
// Log      Date   By  Scan Description
// -----  -------- --- ---- ------------------------------
// 106380 30/04/20 JC  M01  Added ssoLogin
// 108946 11/08/20 JC  M02  Hooked up loginPasswordChangeRequiredType in reducer
// 109897 18/09/20 JC  M03  Un-flag user.isForgottenPassword in forgotten password
// 115273 15/11/21 JC  M04JC  ssoLogin needs to handle expired passwords
//-------------------------------------------------------
*/
import { history } from '../history';
import { fetchActions } from "../FetchWrapper";
import { lifetimeActions } from './Lifetime';
import { passwordActions } from './PasswordManager';

export const loginRequestType = 'LOGIN_REQUEST';
export const loginSuccessType = 'LOGIN_SUCCESS';
export const loginPasswordChangeRequiredType = "LOGIN_PASSWORD_CHANGE_REQUIRED";
export const loginAuthenticationRequiredType = "LOGIN_AUTHENTICATION_REQUIRED";
export const loginFailureType = 'LOGIN_FAILURE';

export const authenticationRequestType = 'AUTHENTICATION_REQUEST';

export const logoutRequestType = 'LOGOUT_REQUEST';
export const logoutSuccessType = 'LOGOUT_SUCCESS';
export const logoutFailureType = 'LOGOUT_FAILURE';

export const ssoLoginSuccessType = 'SSOLOGIN_SUCCESS'; //M01

let user = JSON.parse(localStorage.getItem('user'));

const initialState = user
	? {
		loggedIn: !(user.isPasswordChangeRequired || user.isAuthenticationRequired) ,
		user,
		changingPassword: false,
        loggingOut: false,
        ssoLoggedIn: false, //M01
        loggedOut: false //M01
	}
	: {
		loggedIn: false,
		changingPassword: false,
        loggingOut: false,
        ssoLoggedIn: false, //M01
        loggedOut: false //M01
	};

export const userActions = {
	login,
	forgotPassword,
	authenticate,
	loginCompleted,
	logout,
    sessionExpired,
    ssoLogin //M01
};

function login(employeeCode, password) {
	return (dispatch, getState) => {
		dispatch(_loginRequest());

		const url = getState().clientConfiguration.config.endpoint + "/sessions/login";
		const data = { user: employeeCode, password: password };

		fetchActions.post(url, null, data)
			.then(
				response => {
					let user = response.data;
					// login successful if there's a token in the response
					if (user.ticket) {
						// store user details and token in local storage to keep user logged in between page refreshes
						localStorage.setItem('user', JSON.stringify(user));
					}

					if (user.isAuthenticationRequired) {
						dispatch(_authenticationRequired(user));
						history.push('/authenticate');
                    } else if (user.isPasswordChangeRequired) {
                        //M03 get user isForgottenPassword, protecting against misisng data
                        let isForgottenPassword = false;
                        if (user.isForgottenPassword) {
                            isForgottenPassword = true;
                        }
						dispatch(_passwordChangeRequired(user));
                        dispatch(passwordActions.changeRequired(isForgottenPassword)); //M03
						history.push('/changepassword');
					} else {
						dispatch(_loginSuccess(user));
						history.push('/');
						dispatch(lifetimeActions.onLogIn());
					}
				},
				error => {
					var message = `Could not log in: ${error.message}`;
					dispatch(_loginFailure(message));
				}
			);
	};
}

function forgotPassword(employeeCode, nicNumber, dateOfBirth) {
	return (dispatch, getState) => {
		dispatch(_loginRequest());

		const url = getState().clientConfiguration.config.endpoint + "/sessions/forgottenlogin";

		dateOfBirth = dateOfBirth.replace(/-/g, "/");
		const data = { user: employeeCode, nicnumber: nicNumber, dateofbirth: dateOfBirth};

		fetchActions.post(url, null, data)
			.then(
				response => {
					let user = response.data;
					// login successful if there's a token in the response
					if (user.ticket) {
						// store user details and token in local storage to keep user logged in between page refreshes
						localStorage.setItem('user', JSON.stringify(user));
					}

					if (user.isAuthenticationRequired) {
						dispatch(_authenticationRequired(user));
						history.push('/authenticate');
                    } else if (user.isPasswordChangeRequired) {
                        //M03 get user isForgottenPassword, protecting against misisng data
                        let isForgottenPassword = false;
                        if (user.isForgottenPassword) {
                            isForgottenPassword = true;
                        }
						dispatch(_passwordChangeRequired(user));
                        dispatch(passwordActions.changeRequired(isForgottenPassword)); //M03
						history.push('/changepassword');
					} else {
						dispatch(_loginSuccess(user));
						history.push('/');
						dispatch(lifetimeActions.onLogIn());
					}
				},
				error => {
					var message = `Could not log in: ${error.message}`;
					dispatch(_loginFailure(message));
				}
			);
	};
}

function authenticate(accessCode) {
	return (dispatch, getState) => {
		dispatch(_authenicationRequest());

		const url = getState().clientConfiguration.config.endpoint + "/sessions/authenticate";
		const data = { accessCode: accessCode };
		const ticket = getState().authentication.user.ticket;

		fetchActions.post(url, ticket, data)
			.then(
				response => {
					let user = response.data;
					// store user details and token in local storage to keep user logged in between page refreshes
					if (user.ticket) {
						// store user details and token in local storage to keep user logged in between page refreshes
						localStorage.setItem('user', JSON.stringify(user));
					}

					if (user.isAuthenticationRequired) {
						dispatch(_authenticationRequired(user));
                    } else if (user.isPasswordChangeRequired) {
                        //M03 get user isForgottenPassword, protecting against misisng data
                        let isForgottenPassword = false;
                        if (user.isForgottenPassword) {
                            isForgottenPassword = true;
                        }
						dispatch(_passwordChangeRequired(user));
                        dispatch(passwordActions.changeRequired(isForgottenPassword)); //M03
						history.push('/changepassword');
					} else {
						dispatch(_loginSuccess(user));
						history.push('/');
						dispatch(lifetimeActions.onLogIn());
					}
				},
			error => {
				if (error.sessionExpired) {
					dispatch(sessionExpired());
				}
				else {
					var message = `Could not authenticate: ${error.message}`;
					dispatch(_loginFailure(message));
				}
			}
		);
	};
}

function loginCompleted() {
	return (dispatch, getState) => {
		let user = { ...getState().authentication.user };
		user.isPasswordChangeRequired = false;
        user.isAuthenticationRequired = false;
        user.isForgottenPassword = false; //M03
		localStorage.setItem('user', JSON.stringify(user));

		dispatch(_loginCompleted(user));
		dispatch(lifetimeActions.onLogIn());
	};

	function _loginCompleted(user) { return { type: loginSuccessType, user: user }; }
}

function logout() {
	return (dispatch, getState) => {
		dispatch(_logoutRequest());

		const url = getState().clientConfiguration.config.endpoint + "/sessions/logout";
		const ticket = getState().authentication.user.ticket;

		fetchActions.post(url, ticket)
			.then(
				() => {
                    dispatch(_loggedout());
                    history.push('/'); //M01
				},
				error => {
					if (error.sessionExpired) {
						// Log out failed as session expired? This is somewhat ironic...
						// go ahead and finish log out anyway as session is already invalidated
						dispatch(_loggedout());
					}
					else {
						var message = `Could not log out: ${error.message}`;
						dispatch(_logoutFailure(message));
					}
				}
			);
	};
}

function sessionExpired() {
	return (dispatch) => {
		dispatch(_loggedout());
	};

	function _loggedout() { return { type: logoutSuccessType, message: "Your session has expired. Please log in again to continue" }; }
}

//M01 -- sso Login function, but in reality just calls keep alive with SSO authentication ticket
function ssoLogin(ssoAuthenticationTicket) {
    return (dispatch, getState) => {

        //don't continue if not already got config or authentication
        if (!getState().clientConfiguration || !getState().clientConfiguration.isLoaded || !getState().clientConfiguration.config ||
            !getState().clientConfiguration.config.endpoint) {
            return;
        }

        let loggingIn = getState().authentication.loggingIn;
        let ssoLoggedIn = getState().authentication.ssoLoggedIn;
        let loggedOut = getState().authentication.loggedOut;

        //don't continue if already logging in or sso logged in, or logged out
        if (loggingIn || ssoLoggedIn || loggedOut) {
            return;
        }

        dispatch(_loginRequest(ssoAuthenticationTicket));

        const url = getState().clientConfiguration.config.endpoint + "/sessions/keepalive";
        const ticket = ssoAuthenticationTicket;

        fetchActions.post(url, ticket)
            .then(
                response => {
                    let user = response.data;
                    // login successful if there's a token in the response
                    if (user.ticket) {
                        // store user details and token in local storage to keep user logged in between page refreshes
                        localStorage.setItem('user', JSON.stringify(user));
                    }

					//M04JC run same checks as login (though it will never return is authentication required)
					if (user.isAuthenticationRequired) {
						dispatch(_authenticationRequired(user));
					} else if (user.isPasswordChangeRequired) {
						//M03 get user isForgottenPassword, protecting against misisng data
						let isForgottenPassword = false;
						if (user.isForgottenPassword) {
							isForgottenPassword = true;
						}
						dispatch(_passwordChangeRequired(user));
						dispatch(passwordActions.changeRequired(isForgottenPassword)); //M03
						history.push('/changepassword');
					} else {
						dispatch(_loginSuccess(user));
						history.push('/');
						dispatch(lifetimeActions.onLogIn());
					}
                },
                error => {
                    var message = `Could not log in: ${error.message}`;
                    dispatch(_loginFailure(message));
                }
            );
    };
}

function _loginRequest(ssoAuthenticationTicket) { return { type: loginRequestType, ssoAuthenticationTicket }; } //M01
function _authenicationRequest() { return { type: authenticationRequestType }; }
function _passwordChangeRequired(user) { return { type: loginPasswordChangeRequiredType, user }; }
function _authenticationRequired(user) { return { type: loginAuthenticationRequiredType, user }; }
function _loginSuccess(user) { return { type: loginSuccessType, user }; }
function _loginFailure(error) { return { type: loginFailureType, error }; }

function _logoutRequest() { return { type: logoutRequestType }; }
function _loggedout() { return { type: logoutSuccessType }; }
function _logoutFailure(error) { return { type: logoutFailureType, error }; }

function _ssoLoginSuccess(user) { return { type: ssoLoginSuccessType, user }; } //M01

export const reducer = (state, action) => {
	state = state || initialState;

	// Login

    if (action.type === loginRequestType) {
        return { loggingIn: true, loggedIn: false, ssoLoggedIn: false, loggedOut: false, error: undefined };//M01
    }

	if (action.type === loginSuccessType) {
		return { ...state, loggingIn: false, loggedIn: true, user: action.user, error: undefined };
	}

	if (action.type === loginFailureType) {
		return { ...state, loggingIn: false, error: action.error };
	}

    if (action.type === ssoLoginSuccessType) { //M01s
        return { ...state, loggingIn: false, loggedIn: true, ssoLoggedIn: true, user: action.user, error: undefined };
    }

	// Authentication

	if (action.type === authenticationRequestType) {
		return { ...state, loggingIn: true, loggedIn: false, error: undefined };
    }

    //M02
    if (action.type === loginPasswordChangeRequiredType) {
        return { ...state, loggingIn: false, loggedIn: false, user: action.user, error: action.error };
    }

	if (action.type === loginAuthenticationRequiredType) {
		return { ...state, loggingIn: false, loggedIn: false, user: action.user, error: action.error };
	}

	// Logout

	if (action.type === logoutRequestType) {
		return { ...state, loggingOut: true, error: undefined };
	}

	if (action.type === logoutFailureType) {
		return { ...state, loggingOut: false, error: action.error };
	}

	// Note: The LOGOUT_SUCCESS action is not required to be handled here as it's intercepted by the root reducer in configureStore.js
	// (It will still make it here but the application state is nerfed by the time we see it so there is no need)

	return state;
};